// ---------------------------------------------------------------------------
// Outreach Suggestions engine
// ---------------------------------------------------------------------------
// For every edge in the network where one side is a WARM contact (T1 Customer
// or T2 Quoting) and the other is a COLD prospect (T3 SQL, T4 MQL, T5 No
// Traction), surface a suggestion to ask the warm side for a warm intro.
//
// Tier ordinals mirror data/broker.js:
//   t1 Customer     = 1  (warmest)
//   t2 Quoting      = 2
//   t3 SQL          = 3
//   t4 MQL          = 4
//   t5 No Traction  = 5  (coldest)
// ---------------------------------------------------------------------------

const TIER_ORDINAL = { t1: 1, t2: 2, t3: 3, t4: 4, t5: 5 };

/**
 * Return an array of suggestions sorted by descending priority.
 * Each item has:
 *   id (connection id)
 *   warm (contact object, the warmer side)
 *   cold (contact object, the colder side)
 *   score (positive int; higher is better)
 *   reason (short string)
 *   tier_gap (positive int, e.g. T1→T5 = 4)
 *   target_tier ('t2' | 't3') — what the recommended next stage would be
 */
export function computeOutreachSuggestions(contacts, connections) {
  if (!contacts?.length || !connections?.length) return [];
  const byId = new Map(contacts.map((c) => [c.id, c]));
  const out = [];

  for (const edge of connections) {
    const a = byId.get(edge.contact_a);
    const b = byId.get(edge.contact_b);
    if (!a || !b) continue;
    const oa = TIER_ORDINAL[a.tier];
    const ob = TIER_ORDINAL[b.tier];
    if (!oa || !ob) continue;
    // Skip peer-to-peer at the same tier — nothing to ask for.
    if (oa === ob) continue;
    const warm = oa < ob ? a : b;
    const cold = oa < ob ? b : a;
    const warmOrd = TIER_ORDINAL[warm.tier];
    const coldOrd = TIER_ORDINAL[cold.tier];
    // Only surface plays where the warm side is genuinely warm (T1/T2).
    if (warmOrd > 2) continue;
    const gap = coldOrd - warmOrd;

    // Score:
    //   +4 per tier of gap (T1→T5 = 16 pts)
    //   +5 if the warm side is a Customer (T1) — introductions from customers
    //      carry the most weight in B2B.
    //   +2 if warm & cold share the same firm (internal referral).
    //   +1 if they share the same state (regional resonance).
    let score = gap * 4;
    if (warm.tier === "t1") score += 5;
    if (warm.firm_id && cold.firm_id && warm.firm_id === cold.firm_id) score += 2;
    if (warm.state && cold.state && warm.state === cold.state) score += 1;

    const targetTier = coldOrd - 1 <= 2 ? "t2" : "t3";
    const reason = describe(warm, cold);
    out.push({
      id: edge.id,
      warm,
      cold,
      score,
      reason,
      tier_gap: gap,
      target_tier: targetTier,
      completed_at: edge.signal_completed_at || null,
      completed_by: edge.signal_completed_by || null,
    });
  }

  out.sort((x, y) => y.score - x.score);
  return out;
}

function describe(warm, cold) {
  const wName = warm.name || `${warm.first_name || ""} ${warm.last_name || ""}`.trim() || "this contact";
  const cName = cold.name || `${cold.first_name || ""} ${cold.last_name || ""}`.trim() || "this contact";
  const sameFirm =
    warm.firm_id && cold.firm_id && warm.firm_id === cold.firm_id;
  if (sameFirm) {
    return `${wName} is already a warm contact inside ${cold.company_name || "the same firm"} — ask them to introduce us to ${cName}.`;
  }
  return `Ask ${wName} at ${warm.company_name || "their firm"} to make a warm intro to ${cName} at ${cold.company_name || "their firm"}.`;
}
